import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SmartAssistantMessage } from '@scale/shared/textcollection/smartAssistant';
import cx from 'classnames';
import { makeStyles } from 'design-system/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { Rnd } from 'react-rnd';
import {
  Button,
  CircularProgress,
  IconButton,
  Switch,
  TextInput,
  Tooltip,
} from 'react-scale/components/design-system';
import { SmartAssistantRlhfHook } from 'react-scale/components/scalers/useSmartAssistantRlhf';
import apiFetch from 'react-scale/lib/api-fetch';
import { useCopyToClipboard, useToggle } from 'react-use-v13';
import uuid from 'uuid';

interface SmartAssistantProps {
  prompt?: string;
  assignmentId?: string;
  instructionsS3Document?: string;
  smartAssistantRlhfHook: SmartAssistantRlhfHook;
}

type ModelMessageSnippet = {
  text: string | undefined;
  citationId: string | undefined;
};

const citationRegex = /citation_id=[\w-]+/i;

const generateModelMessageSnippets = (message: { text: string }): ModelMessageSnippet[] => {
  const textSplitBySpans = message.text.split('</span>');
  return textSplitBySpans.map(str => ({
    // this will extract the string starting with "citation_id" as noted above
    // it will then remove the citation_id= portion of the string
    citationId: str.match(citationRegex)?.[0].replace('citation_id=', ''),
    text: str.split('>').slice(1).join('>'), // only split the first > and join it all back
  }));
};

const startMessage = {
  text: "Hi! I'm your smart assistant, how can I help you?",
  isHuman: false,
};

const followupMessage = {
  text: 'Ask me another question!',
  isHuman: false,
};

const errorMessage = {
  text: `Hmm I wasn't able to answer that question. Ask me another question!`,
  isHuman: false,
};

const initialMessages: SmartAssistantMessage[] = [startMessage];

const startHeight = '60vh';
const startWidth = '25vw';
const maxHeight = '95vh';
const maxWidth = '60vw';
// prevent spamming spellbook (only frontend prevention is fine)
const MESSAGES_LIMIT = 5;

const SmartAssistant: React.FC<SmartAssistantProps> = ({
  prompt,
  assignmentId,
  instructionsS3Document,
  smartAssistantRlhfHook,
}) => {
  const classes = useStyles();

  const { rlhfPromptText } = smartAssistantRlhfHook;

  const [assistantOpen, toggleAssistantOpen] = useToggle(false);
  const [showingAssistantHelpMessage, toggleShowingAssistantHelpMessage] = useToggle(true);
  const [applyPrompt, setApplyPrompt] = useState(true);
  const [inputSearch, setInputSearch] = useState<string>('');
  const [messages, setMessages] = useState<SmartAssistantMessage[]>(initialMessages);
  const [loadingAssistantResponse, setLoadingAssistantResponse] = useState(false);
  const [messagesSent, setMessagesSent] = useState(0);

  // instructions specific
  const [instructionsMode, toggleInstructionsMode] = useToggle(false);
  const [instructionsReferences, setInstructionsReferences] = useState<Record<string, string>>({});
  const [instructionsReferenceText, setInstructionsReferenceText] = useState<string | undefined>();

  const containerRef = React.useRef<HTMLDivElement>(null);
  const toolbarRef = React.useRef<HTMLDivElement>(null);
  const interactionRef = React.useRef<HTMLDivElement>(null);
  const conversationEndRef = React.useRef<HTMLDivElement>(null);

  const [disableDragging, setDisableDragging] = useState(false);

  const handleMouseEnter = () => {
    setDisableDragging(true);
  };

  const handleMouseLeave = () => {
    setDisableDragging(false);
  };

  const renderMessage = (text: string, isInstructionsResponse?: boolean) => {
    if (!isInstructionsResponse) {
      return text;
    }
    const modelMessageSnippets = generateModelMessageSnippets({ text }).filter(m => m.text);
    return (
      <div>
        {modelMessageSnippets.map(m => {
          return (
            <span key={m.text}>
              {`${m.text} `}
              {m.citationId && (
                <Tooltip title={instructionsReferenceText} placement="left" arrow>
                  <span>
                    <FontAwesomeIcon
                      style={{ marginRight: 4 }}
                      icon="info-circle"
                      size="sm"
                      color="var(--color-RemoBlue)"
                      onMouseEnter={() => {
                        setInstructionsReferenceText(instructionsReferences[m.citationId!]);
                      }}
                      onMouseLeave={() => setInstructionsReferenceText(undefined)}
                    />
                  </span>
                </Tooltip>
              )}
            </span>
          );
        })}
      </div>
    );
  };

  const [, copy] = useCopyToClipboard();

  const scrollToBottom = () => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setInputSearch(rlhfPromptText);
  }, [rlhfPromptText]);

  useEffect(() => {
    if (instructionsMode) {
      setApplyPrompt(false);
    } else {
      setApplyPrompt(true);
    }
  }, [instructionsMode]);

  const executeLlmSearch = useCallback(
    async (search: any) => {
      const { response } = await apiFetch(null, 'internal/smart-assistant/execute-query', {
        method: 'POST',
        body: {
          query: search,
          prompt: applyPrompt ? prompt : undefined,
          assignmentId,
        },
      });
      return response;
    },
    [applyPrompt, assignmentId, prompt],
  );

  // TODO: Check with Nikash on if this is used anymore
  const executeInstructionSearch = useCallback(
    async (search: any) => {
      const { text, relatedFragments } = await apiFetch(null, 'internal/hermes/chat', {
        method: 'POST',
        body: {
          chatHistory: [
            {
              sender: 'user',
              text: search,
              UUID: uuid.v4(),
            },
          ],
          queryContext: {
            s3AttachmentUrls: [instructionsS3Document],
          },
        },
      });
      const messageReferences = (relatedFragments as Record<string, string>[]).reduce(
        (acc, fragment) => {
          const { body, citationId } = fragment;
          return {
            ...acc,
            [citationId]: body,
          };
        },
        {},
      );
      setInstructionsReferences({
        ...instructionsReferences,
        ...messageReferences,
      });
      return text;
    },
    [instructionsReferences, instructionsS3Document],
  );

  const handleSearch = useCallback(
    async (search: any) => {
      const searchMaybeWithPrompt = prompt && applyPrompt ? `${prompt}\n\n${search}` : search;
      const currentMessages = [...messages, { text: searchMaybeWithPrompt, isHuman: true }];
      setMessages(currentMessages);
      setInputSearch('');
      setLoadingAssistantResponse(true);
      if (!instructionsMode) {
        setMessagesSent(messagesSent + 1);
      }
      try {
        const response = instructionsMode
          ? await executeInstructionSearch(search)
          : await executeLlmSearch(search);
        setLoadingAssistantResponse(false);
        setMessages([
          ...currentMessages,
          { text: response, isHuman: false, isInstructionsResponse: instructionsMode },
          followupMessage,
        ]);
      } catch {
        setLoadingAssistantResponse(false);
        setMessages([...currentMessages, errorMessage]);
      }
    },
    [
      applyPrompt,
      executeInstructionSearch,
      executeLlmSearch,
      messages,
      messagesSent,
      prompt,
      instructionsMode,
    ],
  );

  return (
    <div>
      {!assistantOpen ? (
        <div>
          {showingAssistantHelpMessage && (
            <div className={classes.iconButtonBanner}>
              <div className={classes.row}>
                <div className={classes.iconButtonBannerTitle}>New Smart Assistant</div>
                <div className={classes.right}>
                  <FontAwesomeIcon
                    icon="times"
                    color="white"
                    onClick={toggleShowingAssistantHelpMessage}
                  />
                </div>
              </div>
              Try our new smart assistant to help answer questions about this task!
            </div>
          )}
          <div className={classes.iconButton}>
            <Tooltip title="Open Smart Assistant" placement="left">
              <IconButton onClick={toggleAssistantOpen}>
                <FontAwesomeIcon icon="lightbulb-on" color="var(--color-RemoBlue)" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      ) : (
        <div className={classes.assistantContainer}>
          <Rnd
            opacity={1}
            minWidth={startWidth}
            minHeight={startHeight}
            maxWidth={maxWidth}
            maxHeight={maxHeight}
            disableDragging={disableDragging}
          >
            <div className={classes.assistantContent} ref={containerRef}>
              <div className={cx(classes.row, classes.toolbar)} ref={toolbarRef}>
                <FontAwesomeIcon icon="lightbulb-on" color="white" />
                <p className={classes.title}>
                  <strong>Smart Assistant</strong>
                </p>
                {instructionsS3Document && (
                  <Button className={classes.right} onClick={toggleInstructionsMode} type="primary">
                    {instructionsMode ? 'Search Anything' : 'Search Instructions'}
                  </Button>
                )}
                <Button
                  className={instructionsS3Document ? classes.marginRight : classes.right}
                  onClick={() => setMessages([...initialMessages])}
                  type="primary"
                >
                  Clear
                </Button>
                <FontAwesomeIcon
                  icon="horizontal-rule"
                  color="white"
                  onClick={toggleAssistantOpen}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <div
                className={classes.conversation}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={
                  containerRef.current?.clientHeight &&
                  toolbarRef.current?.clientHeight &&
                  interactionRef.current?.clientHeight
                    ? {
                        height:
                          containerRef.current.clientHeight -
                          toolbarRef.current.clientHeight -
                          interactionRef.current.clientHeight,
                      }
                    : {}
                }
              >
                {[
                  ...messages,
                  ...(loadingAssistantResponse ? [{ isHuman: false, isTyping: true }] : []),
                ].map(message => {
                  return (
                    <div key={message.text} className={cx(classes.row, classes.message)}>
                      {!message.isHuman && (
                        <div className={cx(classes.profileIcon, classes.assistantIcon)}>
                          <FontAwesomeIcon icon="lightbulb-on" color="white" />
                        </div>
                      )}
                      <div
                        className={cx(
                          classes.messageText,
                          message.isHuman && classes.humanMessageText,
                        )}
                      >
                        {!message.isTyping ? (
                          renderMessage(message.text ?? '', message.isInstructionsResponse)
                        ) : (
                          <CircularProgress size={20} />
                        )}
                      </div>
                      {!message.isHuman &&
                        !message.isTyping &&
                        ![startMessage, followupMessage, errorMessage]
                          .map(m => m.text)
                          .includes(message.text ?? '') && (
                          <IconButton className={classes.copyButton}>
                            <FontAwesomeIcon
                              icon="copy"
                              color="var(--color-RemoGray20)"
                              size="sm"
                              onClick={() => copy(message.text ?? '')}
                            />
                          </IconButton>
                        )}
                    </div>
                  );
                })}
                <div ref={conversationEndRef} />
              </div>
              <div>
                <div
                  className={
                    instructionsMode
                      ? cx(classes.interaction, classes.interactionInstruction)
                      : classes.interaction
                  }
                  ref={interactionRef}
                >
                  {prompt && !instructionsMode && (
                    <div>
                      <div className={classes.row} style={{ marginLeft: 12, marginBottom: 8 }}>
                        <Switch
                          checked={applyPrompt}
                          onChange={e => setApplyPrompt(e.target.checked)}
                        />
                        <strong>Prompt</strong>
                      </div>
                      {applyPrompt ? prompt : <s>{prompt}</s>}
                    </div>
                  )}
                  {instructionsMode && (
                    <div>
                      <div className={classes.row}>Currently in instructions-searching mode.</div>
                    </div>
                  )}
                  <div className={classes.row}>
                    <TextInput
                      value={inputSearch}
                      className={classes.input}
                      label={
                        messagesSent >= MESSAGES_LIMIT
                          ? 'Messages limit reached for this session.'
                          : loadingAssistantResponse
                          ? 'Loading...'
                          : 'Ask me anything....'
                      }
                      fullWidth
                      multiline
                      maxRows={5}
                      size="large"
                      onChange={(e: any) => setInputSearch(e.target.value)}
                      onKeyDown={(e: any) => {
                        if (e.key === 'Enter' && !e.shiftKey && e.target.value.trim().length) {
                          handleSearch(inputSearch);
                          e.preventDefault();
                        }
                      }}
                      disabled={loadingAssistantResponse || messagesSent > MESSAGES_LIMIT}
                    ></TextInput>
                    <IconButton className={classes.sendButton} disabled={loadingAssistantResponse}>
                      <FontAwesomeIcon
                        icon="arrow-up"
                        color={
                          loadingAssistantResponse
                            ? 'var(--color-RemoBlue)}'
                            : 'var(--color-RemoGray20)}'
                        }
                        onClick={() => {
                          handleSearch(inputSearch);
                        }}
                      />
                    </IconButton>
                  </div>
                </div>
              </div>
            </div>
          </Rnd>
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles({
  iconButton: {
    position: 'absolute',
    bottom: 16,
    right: 32,
    zIndex: 1,
    backgroundColor: 'white',
    border: '2px solid var(--color-RemoBlue)',
    borderRadius: '50%',
    padding: '4px 0px',
  },
  iconButtonBanner: {
    position: 'absolute',
    bottom: 96,
    right: 32,
    zIndex: 1,
    backgroundColor: 'var(--color-RemoBlue)',
    borderRadius: '16px 16px 0px 16px',
    width: 220,
    color: 'white',
    padding: 16,
    paddingTop: 8,
  },
  iconButtonBannerTitle: {
    fontSize: 16,
    marginBottom: 8,
    marginTop: 8,
  },
  assistantContainer: {
    position: 'absolute',
    bottom: `calc(${startHeight} + 32px)`,
    right: `calc(${startWidth} + 32px)`,
    zIndex: 1,
    pointerEvents: 'auto',
  },
  assistantContent: {
    backgroundColor: 'var(--color-RemoGray01)',
    borderRadius: 8,
    height: '100%',
    width: '100%',
    boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.1)',
    minHeight: startHeight,
    minWidth: startWidth,
    overflow: 'hidden',
  },
  toolbar: {
    padding: '0px 16px',
    width: '100%',
    backgroundColor: 'var(--color-RemoBlue)',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  copyButton: {
    marginLeft: 2,
    padding: 4,
  },
  title: {
    fontSize: 16,
    marginLeft: 16,
    marginTop: 8,
    marginBottom: 8,
    color: 'white',
  },
  right: {
    marginLeft: 'auto',
  },
  interaction: {
    position: 'absolute',
    bottom: 0,
    padding: 16,
    backgroundColor: '#e3f7ff',
    width: '100%',
  },
  interactionInstruction: {
    borderTop: '2px solid var(--color-RemoBlue)',
  },
  input: {
    marginTop: 16,
  },
  conversation: {
    padding: 10,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  profileIcon: {
    borderRadius: '50%',
    padding: 6,
    marginLeft: 8,
    marginRight: 12,
  },
  messageText: {
    backgroundColor: 'white',
    maxWidth: '76%',
    padding: 8,
    borderRadius: '8px 8px 8px 0px',
    boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.1)',
    whiteSpace: 'pre-line',
  },
  humanMessageText: {
    marginLeft: 'auto',
    marginRight: 8,
    borderRadius: '8px 8px 0px 8px',
  },
  assistantIcon: {
    backgroundColor: 'var(--color-RemoBlue)',
  },
  message: {
    margin: '16px 0px',
  },
  sendButton: {
    marginTop: 16,
    marginRight: -12,
    padding: 4,
  },
  marginRight: {
    marginRight: 8,
  },
});

export default SmartAssistant;
